/** Matches `#RGB` and `#RRGGBB` hex color strings, e.g. `#3E79F7`. */
export const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

/**
 * A CSS hex color (`#RGB` or `#RRGGBB`) as a value object: validated on
 * construction, immutable, and the owner of the color math (RGB channels,
 * perceived brightness, shading) so hex parsing is not re-implemented at
 * every usage site.
 *
 * The constructor tolerates a missing leading `#` (legacy stored values);
 * `toString()` always returns the canonical lowercase `#rrggbb` form.
 */
export class HexColor {
  private readonly channels: readonly [number, number, number]

  constructor(input: string) {
    const channels = HexColor.parseChannels(input)

    if (!channels) {
      throw new Error(`Invalid hex color: ${input}`)
    }

    this.channels = channels
  }

  /** Returns the color when `input` is a valid hex color, `undefined` otherwise. */
  static parse(input: string): HexColor | undefined {
    return HexColor.parseChannels(input) ? new HexColor(input) : undefined
  }

  /** Expands 3-digit shorthand and parses the RGB channels; undefined when invalid. */
  private static parseChannels(input: string): [number, number, number] | undefined {
    const digits = input.trim().replace(/^#/, "")

    const sixDigits =
      digits.length === 3
        ? digits
            .split("")
            .map((char) => char + char)
            .join("")
        : digits

    if (!/^[0-9a-fA-F]{6}$/.test(sixDigits)) {
      return undefined
    }

    return [
      parseInt(sixDigits.slice(0, 2), 16),
      parseInt(sixDigits.slice(2, 4), 16),
      parseInt(sixDigits.slice(4, 6), 16),
    ]
  }

  /** The RGB channels as 0-255 numbers. */
  toRgb(): [number, number, number] {
    return [...this.channels]
  }

  /**
   * Perceived brightness from 0 (black) to 255 (white).
   * https://24ways.org/2010/calculating-color-contrast
   */
  brightness(): number {
    const [r, g, b] = this.channels

    return (r * 299 + g * 587 + b * 114) / 1000
  }

  /**
   * Lightens (`amount > 0`) or darkens (`amount < 0`) the color by linearly
   * mixing each channel with white/black. `amount` runs from -1 (black) to 1
   * (white) and is clamped; 0 returns an equal color.
   */
  shade(amount: number): HexColor {
    const target = amount > 0 ? 255 : 0
    const ratio = Math.min(Math.abs(amount), 1)

    const channels = this.channels.map((channel) => Math.round(channel + (target - channel) * ratio))

    return new HexColor(`#${channels.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`)
  }

  /** Canonical lowercase `#rrggbb` form. */
  toString(): string {
    return `#${this.channels.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`
  }
}
